import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { receiptsAPI } from '@matix/api-client';
import type { ReceiptListItem } from '@matix/types';
import { useUI } from '../../store/ui';
import { useCart } from '../../store/cart';
import { useLang } from '../../lib/language';
import { useReprintReceipt } from '../print/useReprintReceipt';
import { formatReceiptDateTime, formatNetValue, computeTotalPages } from '../receipts/helpers';
import { XIcon } from '../../components/icons';

const PAGE_SIZE = 20;

/**
 * Orders (الطلبات) — the cart's selected customer's previous receipts, newest
 * first, with a Print button per row.
 *
 * Reads the ordinary receipts list filtered to the customer (`customer_type` +
 * `customer_id`), so it has the archive's scope — this branch, every shift —
 * and prints through the same reprint path as the Receipts screen.
 *
 * Self-gates on the 'customerOrders' modal id and renders nothing when closed.
 */
export function CustomerOrdersModal(): JSX.Element | null {
  const { t } = useLang();
  const isOpen = useUI((s) => s.openModal === 'customerOrders');
  const closeModal = useUI((s) => s.closeModal);
  const customer = useCart((s) => s.customer);
  const reprint = useReprintReceipt();
  const [page, setPage] = useState(1);
  const [printingId, setPrintingId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) setPage(1);
  }, [isOpen, customer?.id, customer?.type]);

  const query = useQuery({
    queryKey: ['customerOrders', customer?.type, customer?.id, page],
    queryFn: () =>
      receiptsAPI.list({
        scope: 'all',
        customer_type: customer!.type,
        customer_id: customer!.id,
        page,
        limit: PAGE_SIZE,
      }),
    enabled: isOpen && customer != null,
    staleTime: 0,
  });

  if (!isOpen) return null;

  const rows = query.data?.data ?? [];
  const totalPages = computeTotalPages(query.data?.total ?? 0, PAGE_SIZE);

  async function print(id: number): Promise<void> {
    setPrintingId(id);
    try {
      await reprint(id);
    } finally {
      setPrintingId(null);
    }
  }

  return (
    <div style={overlay} onClick={closeModal}>
      <div style={panel} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={header}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{t({ en: 'Orders', ar: 'الطلبات' })}</div>
            {customer && (
              <div style={{ fontSize: 12, color: 'var(--text-muted, #71717a)' }}>
                {customer.name ?? customer.name_ar ?? `#${customer.code ?? customer.id}`}
                {customer.phone ? ` · ${customer.phone}` : ''}
                {query.data ? ` · ${query.data.total} ${t({ en: 'orders', ar: 'طلب' })}` : ''}
              </div>
            )}
          </div>
          <button type="button" style={iconBtn} onClick={closeModal} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>
            <XIcon size={18} />
          </button>
        </div>

        <div style={body}>
          {customer == null ? (
            <Empty text={t({ en: 'Select a customer first.', ar: 'اختر عميلاً أولاً.' })} />
          ) : query.isLoading ? (
            <Empty text={t({ en: 'Loading…', ar: 'جارٍ التحميل…' })} />
          ) : query.isError ? (
            <Empty text={t({ en: "Couldn't load the orders. Check your connection.", ar: 'تعذّر تحميل الطلبات. تحقق من الاتصال.' })} />
          ) : rows.length === 0 ? (
            <Empty text={t({ en: 'This customer has no previous orders at this branch.', ar: 'لا توجد طلبات سابقة لهذا العميل في هذا الفرع.' })} />
          ) : (
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>{t({ en: 'Order #', ar: 'رقم الطلب' })}</th>
                  <th style={th}>{t({ en: 'Date', ar: 'التاريخ' })}</th>
                  <th style={th}>{t({ en: 'Type', ar: 'النوع' })}</th>
                  <th style={th}>{t({ en: 'Payment', ar: 'الدفع' })}</th>
                  <th style={{ ...th, textAlign: 'end' }}>{t({ en: 'Total', ar: 'الإجمالي' })}</th>
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} style={row.is_canceled ? { opacity: 0.55 } : undefined}>
                    <td style={{ ...td, fontWeight: 600 }}>#{row.entry_no}</td>
                    <td style={td}>{formatReceiptDateTime(row.entry_date)}</td>
                    <td style={td}>{typeLabel(row, t)}</td>
                    <td style={td}>{row.payment_label}</td>
                    <td style={{ ...td, textAlign: 'end', fontWeight: 600 }}>{formatNetValue(row.net_value)}</td>
                    <td style={{ ...td, textAlign: 'end' }}>
                      <button
                        type="button"
                        className="receipts-btn receipts-btn-secondary"
                        disabled={printingId != null}
                        onClick={() => void print(row.id)}
                      >
                        {printingId === row.id ? t({ en: 'Printing…', ar: 'جارٍ الطباعة…' }) : t({ en: 'Print', ar: 'طباعة' })}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div style={footer}>
            <button type="button" className="receipts-page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              {t({ en: 'Previous', ar: 'السابقة' })}
            </button>
            <span style={{ fontSize: 13 }}>
              {page} / {totalPages}
            </span>
            <button type="button" className="receipts-page-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
              {t({ en: 'Next', ar: 'التالية' })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function typeLabel(row: ReceiptListItem, t: (s: { en: string; ar: string }) => string): string {
  if (row.is_canceled) return t({ en: 'Voided', ar: 'ملغى' });
  if (row.is_return) return t({ en: 'Return', ar: 'مرتجع' });
  if (row.is_credit) return t({ en: 'Credit sale', ar: 'بيع آجل' });
  return t({ en: 'Sale', ar: 'بيع' });
}

function Empty(props: { text: string }): JSX.Element {
  return (
    <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted, #71717a)' }}>
      {props.text}
    </div>
  );
}

// ── Inline styles (self-contained, same as MembershipModal) ────────────────
const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
};
const panel: React.CSSProperties = {
  width: 'min(820px, 100%)', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
  background: 'var(--surface-1, #fff)', color: 'var(--text, #18181b)',
  borderRadius: 12, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
};
const header: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 18px', borderBottom: '1px solid var(--border, #e4e4e7)',
};
const body: React.CSSProperties = { padding: '8px 16px 16px', overflow: 'auto' };
const footer: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
  padding: '10px 16px', borderTop: '1px solid var(--border, #e4e4e7)',
};
const iconBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: 4,
};
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: React.CSSProperties = {
  textAlign: 'start', padding: '8px 6px', fontWeight: 600, fontSize: 12,
  color: 'var(--text-muted, #71717a)', borderBottom: '1px solid var(--border, #e4e4e7)',
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = {
  padding: '8px 6px', borderBottom: '1px solid var(--border, #e4e4e7)', whiteSpace: 'nowrap',
};
